import { TrendingUp, Calendar, CheckCircle2, AlertCircle } from "lucide-react";
import type { ProjectionResult } from "@/lib/retirement-calc";
import EditableNumber from "./EditableNumber";

interface Props {
  result: ProjectionResult;
  targetAge: number;
  currentAge: number;
  monthlyExpenses: number;
  monthlyContribution: number;
  requiredMonthly: number;
  earliestAge: number | null;
  fmtCompact: Intl.NumberFormat;
  fmtFull: Intl.NumberFormat;
  onTargetAgeChange: (v: number) => void;
  onMonthlyExpensesChange: (v: number) => void;
  /** Editing the nest-egg target back-solves monthly expenses. */
  onNestEggTargetChange: (target: number) => void;
}

const InsightCard = ({
  result,
  targetAge,
  currentAge,
  monthlyExpenses,
  monthlyContribution,
  requiredMonthly,
  earliestAge,
  fmtCompact,
  fmtFull,
  onTargetAgeChange,
  onMonthlyExpensesChange,
  onNestEggTargetChange,
}: Props) => {
  const annualExpenses = monthlyExpenses * 12;
  const target = result.nestEggTargetToday;
  const projected = result.projectedAtRetirementReal;
  const gap = target - projected;
  const onTrack = result.onTrack;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border p-6 sm:p-8 shadow-sm ${
        onTrack
          ? "border-primary/40 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent"
          : "border-border bg-gradient-to-br from-card via-card to-muted/40"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${
            onTrack
              ? "bg-primary/20 text-primary"
              : "bg-destructive/15 text-destructive"
          }`}
        >
          {onTrack ? (
            <CheckCircle2 className="w-3 h-3" />
          ) : (
            <AlertCircle className="w-3 h-3" />
          )}
          {onTrack ? "On track" : "Shortfall"}
        </span>
        <span className="text-[11px] text-muted-foreground">
          Tap any underlined value to edit
        </span>
      </div>

      <p className="text-xl sm:text-2xl lg:text-[28px] font-extrabold leading-snug tracking-tight text-foreground">
        To retire at{" "}
        <EditableNumber
          label="Target retirement age"
          value={targetAge}
          display={String(targetAge)}
          min={Math.max(currentAge + 1, 30)}
          max={80}
          step={1}
          suffix="yrs"
          onCommit={onTargetAgeChange}
        />{" "}
        spending{" "}
        <EditableNumber
          label="Monthly expenses in retirement"
          value={monthlyExpenses}
          display={`${fmtFull.format(monthlyExpenses)}/mo`}
          min={200}
          max={20_000}
          step={50}
          prefix="€"
          hardMax={50_000}
          onCommit={onMonthlyExpensesChange}
        />{" "}
        <span className="text-muted-foreground font-semibold text-base sm:text-lg">
          (~{fmtCompact.format(annualExpenses)}/yr)
        </span>
        , you'll need{" "}
        <EditableNumber
          label="Nest-egg target (25× annual expenses)"
          value={Math.round(target)}
          display={fmtCompact.format(target)}
          min={50_000}
          max={5_000_000}
          step={10_000}
          prefix="€"
          hardMax={50_000_000}
          onCommit={(v) => onNestEggTargetChange(v)}
        />
        .
      </p>

      <p className="mt-3 text-base sm:text-lg text-foreground/80 leading-relaxed">
        {onTrack ? (
          <>
            Keep saving{" "}
            <span className="font-bold tabular-nums">{fmtFull.format(monthlyContribution)}/mo</span>{" "}
            and you'll reach{" "}
            <span className="font-bold tabular-nums">{fmtCompact.format(projected)}</span> by {targetAge} —{" "}
            <span className="font-bold text-primary tabular-nums">
              +{fmtCompact.format(-gap)}
            </span>{" "}
            above target. You're set.
          </>
        ) : Number.isFinite(requiredMonthly) && requiredMonthly > 0 ? (
          <>
            To reach it, save{" "}
            <span className="font-bold text-primary tabular-nums">
              {fmtFull.format(requiredMonthly)}/mo
            </span>{" "}
            (vs your current{" "}
            <span className="tabular-nums">{fmtFull.format(monthlyContribution)}/mo</span>) — that's
            an extra{" "}
            <span className="font-bold tabular-nums">
              {fmtFull.format(Math.max(0, requiredMonthly - monthlyContribution))}/mo
            </span>{" "}
            to close the{" "}
            <span className="tabular-nums">{fmtCompact.format(gap)}</span> gap by {targetAge}.
          </>
        ) : (
          <>
            At your current{" "}
            <span className="font-bold tabular-nums">{fmtFull.format(monthlyContribution)}/mo</span>{" "}
            this target is out of reach by {targetAge}. Try lowering retirement spending, pushing
            the age back, or raising your contribution.
          </>
        )}
      </p>


      {!onTrack && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Number.isFinite(requiredMonthly) && requiredMonthly > 0 && (
            <Suggestion
              icon={<TrendingUp className="w-4 h-4" />}
              label="Increase contribution"
              value={`${fmtFull.format(requiredMonthly)}/mo`}
              detail={`to hit ${fmtCompact.format(target)} by ${targetAge}`}
            />
          )}
          {earliestAge && earliestAge !== targetAge && (
            <Suggestion
              icon={<Calendar className="w-4 h-4" />}
              label="Or retire later"
              value={`Age ${earliestAge}`}
              detail={`at your current ${fmtFull.format(monthlyContribution)}/mo pace`}
            />
          )}
        </div>
      )}
    </div>
  );
};

const Suggestion = ({
  icon,
  label,
  value,
  detail,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
}) => (
  <div className="rounded-xl border border-primary/30 bg-background/70 backdrop-blur-sm p-4">
    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary mb-1">
      {icon}
      {label}
    </div>
    <div className="text-lg sm:text-xl font-extrabold tabular-nums text-foreground leading-tight">
      {value}
    </div>
    <div className="text-xs text-muted-foreground mt-0.5">{detail}</div>
  </div>
);

export default InsightCard;
